import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuthStore } from "@/features/auth/store";
import { apiErrorMessage } from "@/lib/api/errors";
import { openPdfBlob } from "@/lib/pdf-open";

import { helpApi } from "./api";
import { getTour, stepsFor, type Tour } from "./registry";
import { ANONYMOUS_KEY, useHelpStore } from "./store";

/**
 * Charge la mémoire de l'aide de l'utilisateur connecté (ou « anonymous » sur
 * les pages publiques). À monter une fois, au plus haut (App / layouts).
 */
export function useHelpHydration(): void {
  const me = useAuthStore((s) => s.me);
  const hydrate = useHelpStore((s) => s.hydrate);
  useEffect(() => {
    hydrate(me?.id ?? ANONYMOUS_KEY);
  }, [me?.id, hydrate]);
}

/** Un sujet est-il encore « nouveau » (balise pulsante) pour l'utilisateur ? */
export function useTopicIsNew(topicId: string): boolean {
  const beacons = useHelpStore((s) => s.beacons);
  const dismissed = useHelpStore((s) => s.dismissed[topicId]);
  return beacons && !dismissed;
}

/**
 * Démarre une visite : navigue vers sa page de départ si besoin, puis ouvre
 * la première étape. Retourne `false` si la visite est inconnue ou vide pour
 * cet utilisateur.
 */
export function useStartTour(): (tourId: string) => boolean {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useAuthStore((s) => s.me);
  const startTour = useHelpStore((s) => s.startTour);
  return (tourId) => {
    const tour = getTour(tourId);
    if (!tour || stepsFor(tour, me).length === 0) return false;
    if (location.pathname !== tour.route) navigate(tour.route);
    startTour(tourId);
    return true;
  };
}

/** Visite en cours et ses étapes visibles pour l'utilisateur. */
export function useActiveTour(): { tour: Tour; steps: ReturnType<typeof stepsFor>; step: number } | null {
  const active = useHelpStore((s) => s.activeTour);
  const me = useAuthStore((s) => s.me);
  if (!active) return null;
  const tour = getTour(active.id);
  if (!tour) return null;
  return { tour, steps: stepsFor(tour, me), step: active.step };
}

/**
 * Avancement réel de la mise en route de la clinique active : coche d'elle-même
 * les étapes « Premiers pas ». Relu au retour sur l'onglet, pour qu'une étape
 * faite dans un autre onglet (premier patient, première facture) apparaisse.
 */
export function useSetupProgress(enabled = true) {
  const orgId = useAuthStore((s) => s.me?.active_organisation_id ?? null);
  return useQuery({
    queryKey: ["help", "setup-progress", orgId],
    queryFn: helpApi.setupProgress,
    enabled: enabled && !!orgId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Ouvre le guide complet de la plateforme (PDF). Le fichier n'a pas d'adresse
 * publique : il est demandé avec la session, puis ouvert dans un onglet — ou
 * déposé dans les téléchargements si le navigateur bloque la fenêtre.
 */
export function useOpenPlatformGuide() {
  return useMutation({
    mutationFn: helpApi.platformGuide,
    onSuccess: (blob) => {
      if (openPdfBlob(blob, "guide-mada") === "downloaded") {
        toast.info("Le guide a été déposé dans vos téléchargements.");
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Guide indisponible pour le moment.")),
  });
}
